import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  Activity, Database, Cpu, HardDrive, Clock, Users,
  AlertTriangle, CheckCircle, TrendingUp, Building2, Zap,
  RefreshCw, Server
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import platformApi from '@/lib/platformApi';

export default function SystemHealth() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchHealth();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchHealth = async () => {
    try {
      const response = await platformApi.get('/platform/health');
      setHealth(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      toast.error('Failed to load system health');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  const overallStatus = health?.suspended_hospitals > 0 ? 'warning' : 'healthy';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">System Health</h1>
          <p className="text-slate-400">Monitor platform performance and tenant usage</p>
        </div>
        <div className="flex items-center gap-4">
          {lastUpdated && (
            <p className="text-xs text-slate-500">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </p>
          )}
          <Button 
            onClick={fetchHealth}
            variant="outline"
            size="sm"
            className="border-slate-600 text-slate-300 hover:bg-slate-700"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Overall Status Banner */}
      <Card className={`border ${
        overallStatus === 'healthy' 
          ? 'bg-emerald-500/10 border-emerald-500/30' 
          : 'bg-amber-500/10 border-amber-500/30'
      }`}>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            {overallStatus === 'healthy' ? (
              <CheckCircle className="h-8 w-8 text-emerald-400" />
            ) : (
              <AlertTriangle className="h-8 w-8 text-amber-400" />
            )}
            <div>
              <h2 className={`text-lg font-semibold ${
                overallStatus === 'healthy' ? 'text-emerald-400' : 'text-amber-400'
              }`}>
                {overallStatus === 'healthy' ? 'All Systems Operational' : 'Attention Required'}
              </h2>
              <p className="text-sm text-slate-400">
                {overallStatus === 'healthy' 
                  ? 'All services are running normally'
                  : `${health?.suspended_hospitals} hospital(s) suspended`
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Platform Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600">
                <Building2 className="h-6 w-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-white">{health?.active_hospitals || 0}</p>
                <p className="text-xs text-slate-400">Active Hospitals</p>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              of {health?.total_hospitals || 0} total
            </p>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600">
                <Users className="h-6 w-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-white">{health?.total_users?.toLocaleString() || 0}</p>
                <p className="text-xs text-slate-400">Total Users</p>
              </div>
            </div>
            <p className="text-xs text-emerald-400 mt-3 flex items-center gap-1">
              <TrendingUp className="h-3 w-3" />
              Platform wide
            </p>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600">
                <Users className="h-6 w-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-white">{health?.total_patients?.toLocaleString() || 0}</p>
                <p className="text-xs text-slate-400">Total Patients</p>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Across all hospitals
            </p>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="p-3 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600">
                <HardDrive className="h-6 w-6 text-white" />
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-white">{health?.total_storage_used_gb?.toFixed(2) || 0}</p>
                <p className="text-xs text-slate-400">Storage Used (GB)</p>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Database storage
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tenant Health Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Server className="h-5 w-5 text-blue-400" />
            Tenant Resource Usage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-slate-400 text-sm border-b border-slate-700">
                  <th className="pb-3 font-medium">Hospital</th>
                  <th className="pb-3 font-medium">Active Users (24h)</th>
                  <th className="pb-3 font-medium">Storage Used</th>
                  <th className="pb-3 font-medium">Storage Limit</th>
                  <th className="pb-3 font-medium">Usage</th>
                </tr>
              </thead>
              <tbody>
                {health?.tenants?.map((tenant) => {
                  const usagePercent = tenant.storage_limit_mb > 0 
                    ? Math.round((tenant.storage_used_mb / tenant.storage_limit_mb) * 100)
                    : 0;
                  const isHighUsage = usagePercent > 80;
                  
                  return (
                    <tr key={tenant.hospital_id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                      <td className="py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm">
                            {tenant.hospital_name?.charAt(0) || 'H'}
                          </div>
                          <span className="text-white">{tenant.hospital_name}</span>
                        </div>
                      </td>
                      <td className="py-3 text-slate-300">{tenant.active_users_24h}</td>
                      <td className="py-3 text-slate-300">{tenant.storage_used_mb.toFixed(1)} MB</td>
                      <td className="py-3 text-slate-300">{(tenant.storage_limit_mb / 1024).toFixed(0)} GB</td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-2 bg-slate-700 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full transition-all ${
                                isHighUsage ? 'bg-amber-500' : 'bg-emerald-500'
                              }`}
                              style={{ width: `${Math.min(usagePercent, 100)}%` }}
                            />
                          </div>
                          <span className={`text-xs ${isHighUsage ? 'text-amber-400' : 'text-slate-400'}`}>
                            {usagePercent}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {(!health?.tenants || health.tenants.length === 0) && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-slate-500">
                      No tenant data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* System Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Cpu className="h-4 w-4 text-emerald-400" />
              API Performance
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Requests (24h)</span>
              <span className="text-white font-medium">{health?.total_api_requests_24h?.toLocaleString() || 'N/A'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Avg Response Time</span>
              <span className="text-white font-medium">{health?.avg_response_time_ms || 'N/A'} ms</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Uptime</span>
              <span className="text-emerald-400 font-medium">99.9%</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Database className="h-4 w-4 text-purple-400" />
              Database Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Status</span>
              <span className="text-emerald-400 font-medium flex items-center gap-1">
                <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                Connected
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Collections</span>
              <span className="text-white font-medium">18</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Indexes</span>
              <span className="text-white font-medium">Active</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
